import React, { createContext, useContext, useRef, useState } from "react";

// PlayerContext — single source of truth for playback state across
// the three player surfaces (Home, Following, Profile).
//
// What lives in here (Iter-45):
//   - videoRef: the <video> DOM element ref. SHARED — when both home
//     and profile pages reference the same ref, the actual <video>
//     DOM node survives route transitions and playback doesn't reset.
//   - isPlaying / setIsPlaying — the player overlay's visibility flag.
//   - currentVideoIndex / setCurrentVideoIndex — playback position.
//   - bundle / setBundle — page-specific data + handlers bundle. Each
//     page pushes its own version (video list, comment handlers,
//     channel chat aliases, etc) on mount and clears on unmount.
//
// Hoisting the bundle here means PlayerLayout can render a single
// <FeedPlayerOverlay> that survives across route changes, instead of
// each page rendering its own copy.
const PlayerContext = createContext(null);

export const PlayerProvider = ({ children }) => {
  // useRef NEVER changes identity — the <video> DOM node gets attached
  // here and stays attached as the React tree re-renders.
  const videoRef = useRef(null);
  const userPausedRef = useRef(false);
  const failedIdsRef = useRef(new Set());
  const exhaustedToastShownRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [bundle, setBundle] = useState(null);

  const value = {
    videoRef,
    userPausedRef,
    failedIdsRef,
    exhaustedToastShownRef,
    isPlaying, setIsPlaying,
    currentVideoIndex, setCurrentVideoIndex,
    bundle, setBundle,
  };

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

// Hook used by page components. Returns the context value or throws
// loudly if called outside the provider (catches misuse early).
export const usePlayer = () => {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error("usePlayer() must be called inside <PlayerProvider/>");
  }
  return ctx;
};

// Safe variant — returns `null` when called outside <PlayerProvider/>
// instead of throwing. Used by global chrome (e.g. FloatingRecorderFAB)
// that needs to know "is the player overlay open right now?" but lives
// across route boundaries (auth screens, /dashboard, /pricing, etc.)
// where the provider isn't mounted.
export const usePlayerSafe = () => {
  return useContext(PlayerContext) || null;
};
